import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import { FileText, Download, Share2, Trash2, Clock, Sparkles } from "lucide-react";

export default function Reports() {
  const { lang, t } = useI18n();
  const [catalogue, setCatalogue] = useState([]);
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [generating, setGenerating] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);

  const load = async () => {
    try {
      const [c, r, tpl] = await Promise.all([
        api.get("/reports/catalogue"),
        api.get("/reports/"),
        api.get("/white-label/templates"),
      ]);
      setCatalogue(c.data.items || []);
      setReports(r.data.items || []);
      setTemplates(tpl.data.items || []);
      if (!selectedType && c.data.items?.length) setSelectedType(c.data.items[0].key);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const generate = async () => {
    if (!selectedType) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate", {
        report_type: selectedType,
        format: "pdf",
        template_id: selectedTemplate || null,
        lang,
      });
      toast.success(lang === "fr" ? `Rapport généré : ${data.title}` : `Report generated: ${data.title}`);
      await load();
    } catch {
      toast.error(lang === "fr" ? "Erreur de génération" : "Generation error");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async (r) => {
    try {
      const resp = await api.get(`/reports/${r.report_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `altiora-${r.report_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(lang === "fr" ? "Export PDF indisponible pour ce type (V2)" : "PDF export unavailable for this type (V2)");
    }
  };

  const share = async (r) => {
    try {
      const { data } = await api.post(`/reports/${r.report_id}/share`, { ttl_hours: 168, max_downloads: 10 });
      const shareUrl = `${window.location.origin}/share/${data.token}`;
      await navigator.clipboard.writeText(shareUrl).catch(() => {});
      setShareInfo({ token: data.token, url: shareUrl, expires_at: data.expires_at, max_downloads: data.max_downloads });
      toast.success(lang === "fr" ? "Lien copié — expire dans 7 jours" : "Link copied — expires in 7 days");
    } catch {
      toast.error(lang === "fr" ? "Erreur de partage" : "Share error");
    }
  };

  const remove = async (r) => {
    if (!confirm(lang === "fr" ? "Supprimer ce rapport ?" : "Delete this report?")) return;
    await api.delete(`/reports/${r.report_id}`);
    await load();
  };

  const meta = catalogue.find((m) => m.key === selectedType);

  return (
    <div className="min-h-full" data-testid="reports-root">
      <TopBar
        title={lang === "fr" ? "Report Intelligence Engine™" : "Report Intelligence Engine™"}
        subtitle={lang === "fr" ? "Rapports professionnels — PDF, résumé exécutif IA, partage sécurisé." : "Professional reports — PDF, AI executive summary, secure sharing."}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 space-y-8">
        {/* Generator card */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6 md:p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-2">
            {lang === "fr" ? "Nouveau rapport" : "New report"}
          </div>
          <div className="font-display text-xl text-[#1F2937] tracking-tight">
            {lang === "fr" ? "Générer un rapport" : "Generate a report"}
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                {lang === "fr" ? "Type de rapport" : "Report type"}
              </label>
              <select
                data-testid="reports-type-select"
                value={selectedType || ""}
                onChange={(e) => setSelectedType(e.target.value)}
                className="mt-2 w-full bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
              >
                {catalogue.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}{m.pdf_ready ? " · PDF" : " · JSON"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                {lang === "fr" ? "Modèle (optionnel)" : "Template (optional)"}
              </label>
              <select
                data-testid="reports-template-select"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="mt-2 w-full bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
              >
                <option value="">— {lang === "fr" ? "Aucun" : "None"} —</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name} · {t.sector}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                data-testid="reports-generate-btn"
                onClick={generate}
                disabled={generating}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.8} />
                {generating ? t("common.loading") : (lang === "fr" ? "Générer" : "Generate")}
              </button>
            </div>
          </div>
          {meta && (
            <div className="mt-6 rounded-md border border-[#E4E1D8]/60 bg-[#F8F7F3]/60 p-4">
              <div className="text-xs text-[#5B5B62] leading-relaxed">{meta.description}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(meta.formats || []).map((f) => (
                  <span key={f} className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#E4E1D8] text-[#5B5B62]">
                    {f}
                  </span>
                ))}
                {meta.pdf_ready && (
                  <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#2E8B57]/40 text-[#2E8B57] bg-[#2E8B57]/10">
                    PDF ready
                  </span>
                )}
                {meta.anonymised && (
                  <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#4682B4]/40 text-[#4682B4] bg-[#4682B4]/10">
                    {lang === "fr" ? "Anonymisé" : "Anonymised"}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Share modal-lite */}
        {shareInfo && (
          <div className="rounded-md border border-[#0B2545]/40 bg-[#0B2545]/5 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545]">
                {lang === "fr" ? "Lien sécurisé copié" : "Secure link copied"}
              </div>
              <div className="mt-1 text-xs text-[#1F2937] font-mono truncate max-w-[720px]">{shareInfo.url}</div>
              <div className="mt-1 text-[10px] text-[#5B5B62] font-mono">
                {lang === "fr" ? "Expire" : "Expires"}: {new Date(shareInfo.expires_at).toLocaleString()} · {shareInfo.max_downloads} {lang === "fr" ? "téléchargements" : "downloads"}
              </div>
            </div>
            <button
              onClick={() => setShareInfo(null)}
              className="text-xs text-[#5B5B62] hover:text-[#1F2937]"
            >
              {lang === "fr" ? "Fermer" : "Close"}
            </button>
          </div>
        )}

        {/* Reports list */}
        <div>
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="font-display text-xl tracking-tight text-[#1F2937]">
              {lang === "fr" ? "Rapports générés" : "Generated reports"}
            </h2>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
              {reports.length} · {lang === "fr" ? "au total" : "total"}
            </div>
          </div>
          {reports.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#E4E1D8] bg-[#F8F7F3]/40 p-10 text-center">
              <FileText className="h-6 w-6 text-[#5B5B62] mx-auto" strokeWidth={1.5} />
              <div className="mt-3 text-sm text-[#5B5B62]">
                {lang === "fr" ? "Aucun rapport pour l'instant." : "No reports yet."}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reports.map((r) => (
                <div key={r.report_id} data-testid="reports-list-item" className="rounded-md border border-[#E4E1D8] bg-white p-4 hover:border-[#B8B5AC] transition-all">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#1F2937]">{r.title}</div>
                      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                        {r.report_type}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-mono text-[#5B5B62]">
                      <Clock className="h-3 w-3" />
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      data-testid="reports-pdf-btn"
                      onClick={() => downloadPdf(r)}
                      className="inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#DCC29A]"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={1.8} /> PDF
                    </button>
                    <button
                      data-testid="reports-share-btn"
                      onClick={() => share(r)}
                      className="inline-flex items-center gap-1 text-xs text-[#5B5B62] hover:text-[#1F2937]"
                    >
                      <Share2 className="h-3.5 w-3.5" strokeWidth={1.8} /> {lang === "fr" ? "Partager" : "Share"}
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="ml-auto inline-flex items-center gap-1 text-xs text-[#8B0000] hover:text-[#D97706]"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
